import { duplicateSync, moveInOrOut } from '../../action';
import { removeOnlyOneChildGroup, updateGroupSize } from '../../action/group';
import { EventTypes, TYPES } from '../../render/constants';
import { getOrCreateSnapEngine } from '../align';
import { Rect } from '../region';

export function regionMoveAction(hitTestContext) {
  const provider = hitTestContext.provider;
  if (hitTestContext.type === EventTypes.down && hitTestContext.targetRegion) {
    provider.pointStart = {
      ...hitTestContext.point,
    };
    const topNodeIds = hitTestContext.targetRegion.nodes.filter((n) => n.parentId).map((n) => n.id);
    provider.movingNodes = hitTestContext.targetRegion.nodes
      .filter((node) => node.type !== TYPES.frame)
      .filter((n) => {
        if (n.parentId && topNodeIds.includes(n.parentId)) return false;
        return true;
      })
      .map((node) => {
        return {
          id: node.id,
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h,
          node: node,
        };
      });
    // Prepare union rect and start snap engine for region move
    const stageContext = hitTestContext.stageContext;
    if (provider.movingNodes.length) {
      const union = stageContext.api.getNodeRegion(provider.movingNodes[0].node).rect;
      for (let i = 1; i < provider.movingNodes.length; i++) {
        const r = stageContext.api.getNodeRegion(provider.movingNodes[i].node).rect;
        union.expandRect(r);
      }
      provider.movingInitialUnionRect = { x: union.x, y: union.y, w: union.w, h: union.h };
      const snap = getOrCreateSnapEngine(stageContext);
      snap.end();
      snap.start({
        box: union,
        movingNodes: provider.movingNodes,
        deltaX: 0,
        deltaY: 0,
        point: null,
        operation: 'move',
      });
    }
    return true;
  }
}

export function moveAction(hitTestContext) {
  const provider = hitTestContext.provider;
  if (hitTestContext.type === EventTypes.down) {
    const selectedNodes = hitTestContext.selectedNodes;
    const node = hitTestContext.targetSelectedNode || hitTestContext.targetNode;
    const isTargetInSelection = selectedNodes.includes(node);
    if (isTargetInSelection) {
      provider.pointStart = {
        ...hitTestContext.point,
      };

      // Filter out group children when moving groups to avoid alignment conflicts
      // Build a Set of all descendant ids of selected groups (computed once)
      const stageApi = hitTestContext.stageContext.api;
      const selectedGroupDescendantIdSet = (() => {
        const set = new Set();
        for (const sel of selectedNodes) {
          if (sel.type === TYPES.group) {
            const descendants = stageApi.getDescendants(sel.id);
            for (const d of descendants) set.add(d.id);
          }
        }
        return set;
      })();
      const filteredSelectedNodes = selectedNodes.filter((sel) => {
        if (sel.type === TYPES.group) return true;
        return !selectedGroupDescendantIdSet.has(sel.id);
      });

      provider.movingNodes = filteredSelectedNodes
        .filter((node) => !hitTestContext.stageContext.api.shouldSkip(node))
        .filter((node) => node.type !== TYPES.frame)
        .map((node) => {
          return {
            id: node.id,
            x: node.x,
            y: node.y,
            w: node.w,
            h: node.h,
            node: node,
          };
        });

      // Cache initial union rect for alignment (translation-only during move)
      if (provider.movingNodes.length) {
        const first = filteredSelectedNodes[0];
        const union = hitTestContext.stageContext.api.getNodeRegion(first).rect;
        for (let i = 1; i < filteredSelectedNodes.length; i++) {
          const r = hitTestContext.stageContext.api.getNodeRegion(filteredSelectedNodes[i]).rect;
          union.expandRect(r);
        }
        provider.movingInitialUnionRect = { x: union.x, y: union.y, w: union.w, h: union.h };
        // Start snap engine for this gesture
        const stageContext = hitTestContext.stageContext;
        const snap = getOrCreateSnapEngine(stageContext);
        snap.end();
        snap.start({
          box: union,
          movingNodes: provider.movingNodes,
          deltaX: 0,
          deltaY: 0,
          point: null,
          operation: 'move',
        });
      } else {
        provider.movingInitialUnionRect = null;
      }
      if (hitTestContext.targetSelectedNode) return true;
      return false;
    }
  } else if (hitTestContext.type === EventTypes.up) {
    if (provider.movingNodes && provider.movingNodes.length && provider.pointStart) {
      const { selectedNodes, depthNode } = hitTestContext;
      const isTargetInSelection = selectedNodes.includes(depthNode);
      const isNotMove =
        Math.abs(hitTestContext.point.x - provider.pointStart.x) < 3 &&
        Math.abs(hitTestContext.point.y - provider.pointStart.y) < 3;
      const { stageContext } = hitTestContext;
      const { action } = stageContext.providers;
      if (isTargetInSelection && isNotMove && action.isShiftKeyPressed) {
        stageContext.api.unselectNode(depthNode.id);
        return true;
      }

      // Smart Duplicate Tracking: Record move delta if we moved
      if (!isNotMove) {
        const node = provider.movingNodes[0];
        const initial = provider.getMovingNodeInitialBox(node.id);
        // Current position is in the node object itself (updated in move loop)
        const current = node.node;
        // Note: provider.movingNodes contains copies but references real nodes in .node property
        // But getMovingNodeInitialBox returns the cached initial state.

        // Calculate delta based on first node (assuming all moved together)
        const dx = current.x - initial.x;
        const dy = current.y - initial.y;

        provider.smartDuplicateDelta = { x: dx, y: dy };
        provider.lastMovedIds = provider.movingNodes.map((n) => n.id);
      }
    }
    // Reset snap engine and align caches on gesture end
    const stageContext = hitTestContext.stageContext;
    getOrCreateSnapEngine(stageContext).end();
    provider.alignCache = null;
    // Keep alignment result for a short moment to show final alignment
    setTimeout(() => {
      if (hitTestContext.stageContext.interactive) {
        hitTestContext.stageContext.interactive.alignmentResult = null;
        hitTestContext.stageContext.api.markNeedRender();
      }
    }, 200);
    return false;
  } else if (hitTestContext.type === EventTypes.move) {
    if (provider.movingNodes && provider.movingNodes.length && provider.pointStart) {
      const stageContext = hitTestContext.stageContext;
      const { action } = stageContext.providers;
      let moveX = Math.round(hitTestContext.point.x - provider.pointStart.x);
      let moveY = Math.round(hitTestContext.point.y - provider.pointStart.y);

      if (action.isAltKeyPressed && !provider.altCreated) {
        provider.altCreated = true;
        const targetNodes = provider.movingNodes.map((n) => n.node);
        const newNodes = duplicateSync(stageContext, targetNodes);

        // 更新选中状态，确保新复制的节点被选中
        stageContext.api.selectNode(newNodes.map((n) => n.id));

        provider.movingNodes = newNodes.map((node) => {
          provider.getMovingNodeInitialBox(node.id);
          return {
            id: node.id,
            x: node.x,
            y: node.y,
            w: node.w,
            h: node.h,
            node: node,
          };
        });
        // Recompute cached union rect for new duplicates
        if (provider.movingNodes.length) {
          const first = newNodes[0];
          const union = stageContext.api.getNodeRegion(first).rect;
          for (let i = 1; i < newNodes.length; i++) {
            const r = stageContext.api.getNodeRegion(newNodes[i]).rect;
            union.expandRect(r);
          }
          provider.movingInitialUnionRect = {
            x: union.x,
            y: union.y,
            w: union.w,
            h: union.h,
          };
          // Restart snap engine with new moving nodes
          const snap = getOrCreateSnapEngine(stageContext);
          snap.end();
          snap.start({
            box: union,
            movingNodes: provider.movingNodes,
            deltaX: moveX,
            deltaY: moveY,
            point: null,
            operation: 'move',
          });
        }
      }
      if (provider.altCreated) {
        provider.cursor.setById('copy');
      }

      // Use cached union rect and translate by current delta (avoids per-move region recomputation)
      let box;
      if (provider.movingInitialUnionRect) {
        box = new Rect(
          provider.movingInitialUnionRect.x + moveX,
          provider.movingInitialUnionRect.y + moveY,
          provider.movingInitialUnionRect.w,
          provider.movingInitialUnionRect.h
        );
      } else {
        // Fallback for safety
        const r0 = stageContext.api.getNodeRegion(provider.movingNodes[0].node).rect;
        box = new Rect(r0.x + moveX, r0.y + moveY, r0.w, r0.h);
      }

      const { action: actionProvider } = stageContext.providers;

      let result = null;
      if (!actionProvider.isMetaKeyPressed) {
        const snap = getOrCreateSnapEngine(stageContext);
        if (!snap.active) {
          snap.start({
            box,
            movingNodes: provider.movingNodes,
            deltaX: moveX,
            deltaY: moveY,
            point: null,
            operation: 'move',
          });
        }
        result = snap.update({ box, deltaX: moveX, deltaY: moveY, point: null });
      }

      if (result) {
        moveX = result.delta.left;
        moveY = result.delta.top;
        stageContext.interactive.alignmentResult = result;
      } else {
        stageContext.interactive.alignmentResult = null;
      }

      // Ensure re-render to update alignment lines
      stageContext.api.markNeedRender();

      // Batch updates in a single transaction to reduce overhead
      let effectsGroups = [];
      stageContext.api.transact(() => {
        provider.movingNodes.forEach((node) => {
          const cache = provider.getMovingNodeInitialBox(node.id);
          provider.stageContext.api.updateNode(node.id, {
            x: cache.x + moveX,
            y: cache.y + moveY,
          });
        });

        updateGroupSize(stageContext, provider.movingNodes);
        effectsGroups = moveInOrOut(hitTestContext, provider.movingNodes);
      });

      if (effectsGroups && effectsGroups.length) {
        updateGroupSize(
          stageContext,
          effectsGroups.map((group) => {
            return {
              node: {
                parentId: group.id,
              },
            };
          })
        );
        removeOnlyOneChildGroup(stageContext, effectsGroups);
      }

      provider.onChange && provider.onChange();
      return true;
    }
  }
}
